// Copyright 2025. WebPros International GmbH. All rights reserved.

import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { RootState } from 'admin/core/store';
import { Loader } from 'common/components';
import {
    Action,
    Button,
    Icon,
    Toolbar,
    ToolbarExpander,
    Translate,
} from '@plesk/ui-library';
import PageHeader from 'common/components/PageHeader/PageHeader';
import { WithErrorPages } from 'common/components/WithPageNotFound/WithErrorPages';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceActions from 'admin/computeResource/actions';
import * as computeResourceVmActions from 'common/modules/computeResourceVm/actions';
import { connect } from 'react-redux';
import List from 'common/components/List/List';
import { ListColumn } from '@plesk/ui-library/types/src/components/List';
import { IVmResponse } from 'common/api/resources/ComputeResourceVm';
import {
    convertToDataUnit,
    DataUnit,
} from 'common/helpers/units';
import { formatTableDate } from 'common/date';
import {
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    MainTitleWithActionSubContainer,
    PageName,
    PageSection,
} from 'common/components/PageHeader/Styles';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import { Dialog } from 'common/components/Dialog/Dialog';
import StartDisasterRecoveryModal from 'admin/disasterRecovery/components/StartDisasterRecoveryModal/StartDisasterRecoveryModal';
import { readiness } from 'common/api/resources/DisasterRecovery';
import {
    IComputeResourceRecoveryReadinessStatusResponse,
    IComputeResourceVmForDisasterRecovery,
} from 'common/api/resources/DisasterRecovery/model';
import { useIsFirstLoading } from 'common/hooks/useIsFirstLoading';

type IProps =
    RouteComponentProps<{ computeResourceId: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const TEST = {
    recoverSelectedBtn: 'recover-selected-btn',
    selectDREnabled: 'select-dr-enabled-btn',
    selectBackupAvail: 'select-backup-avail',
};

function isBackupExistsAndDREnabled(vm: IVmResponse): boolean {
    return !!vm.last_backup_date && vm.is_disaster_recovery_enabled;
}

function isBackupExists(vm: IVmResponse): boolean {
    return !!vm.last_backup_date;
}

export function ComputeResourceRecoveryPage({
    match: {
        params: {
            computeResourceId,
        },
    },
    computeResourceActions: {
        getComputeResource,
    },
    computeResourceVmActions: {
        getComputeResourceVms,
    },
    computeResource,
    computeResourceVms,
    isLoading,
    history,
}: IProps) {

    const [selected, setSelected] = useState<number[]>([]);

    const isFirstLoading = useIsFirstLoading(isLoading);

    const [isFormOpen, setIsFormOpen] = useState(false);

    const [readinessStatus, setReadinessStatus] = useState<IComputeResourceRecoveryReadinessStatusResponse>();

    const columns: Array<ListColumn<IVmResponse>> = useMemo(() => [
        {
            key: 'name',
            title: <Translate content={'disasterRecovery.recoveryPage.table.name'} />,
        },
        {
            key: 'backup_date',
            title: <Translate content={'disasterRecovery.recoveryPage.table.lastBackupDate'} />,
            render: (row) => row.last_backup_date
                ? formatTableDate(row.last_backup_date)
                : '',
        },
        {
            key: 'cpu',
            title: <Translate content={'disasterRecovery.recoveryPage.table.cpu'} />,
            align: 'right',
            render: (row) => row.specifications.vcpu,
        },
        {
            key: 'ram',
            title: <Translate content={'disasterRecovery.recoveryPage.table.ram'} />,
            align: 'right',
            render: (row) => `${convertToDataUnit(row.specifications.ram, DataUnit.GiB)} GiB`,
        },
        {
            key: 'disk',
            title: <Translate content={'disasterRecovery.recoveryPage.table.disk'} />,
            align: 'right',
            render: (row) => `${row.specifications.disk} GiB`,
        },
        {
            key: 'dr',
            title: <Translate content={'disasterRecovery.recoveryPage.table.dr'} />,
            align: 'center',
            render: (row) => row.is_disaster_recovery_enabled
                ? <Icon name={'check-mark'} intent={'success'} />
                : <Icon name={'cross-mark'} intent={'danger'} />,
        },
    ], []);

    useEffect(() => {
        const getData = async () => {
            const response = await readiness.computeResourceReadiness(computeResourceId);

            setReadinessStatus(response.data.data);
        };

        getData();
    }, [computeResourceId]);

    const selectedServers = useMemo(
        (): IComputeResourceVmForDisasterRecovery[] =>
            selected.map(vmId => {
                const computeResourceVm = computeResourceVms.data.find(item => item.id === vmId)!;

                return {
                    computeResourceVm,
                    requirements: {
                        vcpu: computeResourceVm.specifications.vcpu,
                        ram: computeResourceVm.specifications.ram,
                        disk: computeResourceVm.specifications.disk,
                    },
                };
            }),
        [selected, computeResourceVms]
    );

    const totals = useMemo(() => ({
        withBackups: computeResourceVms.data.filter(isBackupExists).length,
        withDR: computeResourceVms.data.filter(isBackupExistsAndDREnabled).length,
    }), [computeResourceVms]);

    const selectedTotals = useMemo(() => ({
        withBackups: selectedServers.filter(server => isBackupExists(server.computeResourceVm)).length,
        withDR: selectedServers.filter(server => isBackupExistsAndDREnabled(server.computeResourceVm)).length,
    }), [selectedServers]);


    const isDREnabledTotalSelected = totals.withDR === selectedTotals.withDR;

    const isBackupAvailTotalSelected = totals.withBackups === selectedTotals.withBackups;

    const onMount = async () => await Promise.all([
        getComputeResource(+computeResourceId),
        getComputeResourceVms({
            filters: {
                compute_resource_id: +computeResourceId,
            },
            per_page: 'all',
        }),
    ]);

    function handleSelectionChange(selection: number[]) {
        setSelected(() => selection.filter(vmId => {
            const vm = computeResourceVms.data.find(v => v.id === vmId);

            return !!vm?.last_backup_date;
        }));
    }

    function handleToggleDREnabled() {
        const drEnabledVmIds = computeResourceVms.data
            .filter(isBackupExistsAndDREnabled)
            .map(vm => vm.id);

        const newSelected = isDREnabledTotalSelected
            ? selected.filter(id => !drEnabledVmIds.includes(id))
            : [...selected, ...drEnabledVmIds.filter(id => !selected.includes(id))];

        setSelected(newSelected);
    }

    function handleToggleBackupAvail() {
        const backupAvailVmIds = computeResourceVms.data
            .filter(isBackupExists)
            .map(vm => vm.id);

        const newSelected = isBackupAvailTotalSelected
            ? selected.filter(id => !backupAvailVmIds.includes(id))
            : [...selected, ...backupAvailVmIds.filter(id => !selected.includes(id))];

        setSelected(newSelected);
    }

    function handleRecoverSelected() {
        setIsFormOpen(true);
    }

    function handleSubmit() {
        setIsFormOpen(false);
        history.push(pathTo('disaster_recovery/history'));
    }

    return (
        <>
            <WithErrorPages onMount={onMount} deps={[computeResourceId]}>
                <Loader isLoading={isFirstLoading}>
                    <PageHeader
                        title={
                            <MainTitleWithActionSubContainer>
                                <PageSection>
                                    <Action
                                        component={Link}
                                        to={pathTo('disaster_recovery')}
                                    >
                                        <Translate content={'disasterRecovery.recoveryPage.title'} />
                                    </Action>
                                    <Icon name="chevron-right" />
                                </PageSection>
                                <PageName>
                                    <span>
                                        {computeResource.name}
                                    </span>
                                </PageName>
                            </MainTitleWithActionSubContainer>
                        }
                    />

                    <List
                        isLoading={isFirstLoading}
                        data={computeResourceVms.data}
                        columns={columns}
                        rowKey={'id'}
                        selection={selected}
                        onSelectionChange={handleSelectionChange}
                        rowProps={
                            (row) => (!row.last_backup_date)
                                ? { className: 'disabled-row', 'data-cy': `compute-resource-${row.id}` }
                                : { 'data-cy': `compute-resource-${row.id}` }
                        }
                        toolbar={(
                            <Toolbar>
                                <div style={{ marginRight: 5 }}>
                                    <Translate content="disasterRecovery.recoveryPage.buttons.buttonsTitle" />
                                </div>

                                <Button
                                    onClick={handleToggleDREnabled}
                                    data-cy={TEST.selectDREnabled}
                                    intent={totals.withDR && isDREnabledTotalSelected ? 'primary' : undefined}
                                    icon={totals.withDR && isDREnabledTotalSelected ? <Icon name={'check-mark'} /> : undefined}
                                >
                                    <Translate content={'disasterRecovery.recoveryPage.buttons.selectDREnabled'} />
                                </Button>

                                <Button
                                    onClick={handleToggleBackupAvail}
                                    data-cy={TEST.selectBackupAvail}
                                    intent={totals.withBackups && isBackupAvailTotalSelected ? 'primary' : undefined}
                                    icon={totals.withBackups && isBackupAvailTotalSelected ? <Icon name={'check-mark'} /> : undefined}
                                >
                                    <Translate content={'disasterRecovery.recoveryPage.buttons.selectBackupAvail'} />
                                </Button>

                                <ToolbarExpander />

                                <Button
                                    intent="primary"
                                    onClick={handleRecoverSelected}
                                    disabled={!selected.length}
                                    data-cy={TEST.recoverSelectedBtn}
                                >
                                    <Translate content={'disasterRecovery.recoveryPage.buttons.recoverSelected'} />
                                </Button>
                            </Toolbar>
                        )}
                    />
                </Loader>
            </WithErrorPages>

            <Dialog
                isOpen={isFormOpen}
                closeHandler={() => setIsFormOpen(false)}
                size={'sm'}
                heading={<Translate content="disasterRecovery.recoveryPage.dialog.title" />}
            >
                {readinessStatus && (
                    <StartDisasterRecoveryModal
                        servers={selectedServers}
                        disasterRecoveryReadinessStatus={readinessStatus}
                        onSubmit={handleSubmit}
                    />
                )}
            </Dialog>
        </>
    );
}

const mapStateToProps = (state: RootState) => ({
    computeResource: state.computeResource.item,
    computeResourceVms: state.computeResourceVm.list,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_ITEM) ||
        state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_LIST),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
    computeResourceVmActions: bindActionCreators(computeResourceVmActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ComputeResourceRecoveryPage));
